"use client";

import { useState, useRef, useEffect, Suspense } from "react";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BlurFade } from "@/components/ui/blur-fade";
import { TextAnimate } from "@/components/ui/text-animate";
import { BackgroundBeams } from "@/components/ui/background-beams";
import { DotPattern } from "@/components/ui/dot-pattern";
import { useTranslation } from "@/lib/i18n/context";
import posthog from "posthog-js";

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginPageInner />
    </Suspense>
  );
}

function LoginPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (searchParams.get("pending") === "1") {
      toast.error(t.auth.login.pendingError);
    }
  }, [searchParams, t.auth.login.pendingError]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        // Check if user is pending
        try {
          const res = await fetch(`/api/auth/check-status?email=${encodeURIComponent(email)}`);
          const data = await res.json();
          if (data.pending) {
            toast.error(t.auth.login.pendingError);
          } else {
            toast.error(t.auth.login.error);
          }
        } catch {
          toast.error(t.auth.login.error);
        }
      } else {
        posthog.capture("user_logged_in", { method: "credentials" });
        router.push("/today");
        router.refresh();
      }
    } catch {
      toast.error(t.auth.login.genericError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative">
      <DotPattern
        className="absolute inset-0 text-primary/10 z-0"
        width={24}
        height={24}
        cr={1}
      />
      <BackgroundBeams className="z-0" />
      <BlurFade delay={0.1} className="relative z-10">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
              <BookOpen className="h-6 w-6 text-primary-foreground" />
            </div>
            <CardTitle className="text-3xl font-display font-bold tracking-tight">
              Journ<span className="text-primary">ai</span>l
            </CardTitle>
            <CardDescription>{t.auth.login.subtitle}</CardDescription>
          </CardHeader>
          <form onSubmit={onSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t.auth.login.email}</Label>
                <Input
                  ref={emailRef}
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t.auth.login.emailPlaceholder}
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t.auth.login.password}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  disabled={loading}
                />
                <div className="flex justify-end">
                  <Link
                    href="/forgot-password"
                    className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                  >
                    {t.auth.login.forgotPassword}
                  </Link>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t.auth.login.submit}
              </Button>

              <div className="flex items-center gap-3 w-full text-xs text-muted-foreground">
                <div className="flex-1 h-px bg-border" />
                {t.common.or}
                <div className="flex-1 h-px bg-border" />
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={loading}
                onClick={() => signIn("google", { callbackUrl: "/today" })}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.72 4.1-5.5 4.1-3.3 0-6-2.73-6-6.1 0-3.37 2.7-6.1 6-6.1 1.88 0 3.14.8 3.86 1.48l2.63-2.53C16.78 3.37 14.6 2.4 12 2.4 6.9 2.4 2.8 6.5 2.8 11.6s4.1 9.2 9.2 9.2c5.3 0 8.8-3.72 8.8-8.96 0-.6-.06-1.06-.14-1.64H12z"/>
                </svg>
                {t.auth.login.googleSignIn}
              </Button>

              <p className="text-sm text-muted-foreground">
                {t.auth.login.noAccount}{" "}
                <Link href="/register" className="text-primary underline-offset-4 hover:underline">
                  {t.auth.login.register}
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </BlurFade>
    </div>
  );
}
